// src\themes\tolica\components\GridItems.tsx
'use client';

import { Heading } from '@/components/ui/Heading';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';

import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

interface GridItem {
    title: string;
    url?: string;
    orientation?: "horizontal" | "vertical" | "square"
    image?: {
        id: number;
        url: string;
        name: string;
    };
}

interface GridItemsProps {
    data: {
        items: GridItem[];
    };
    isHome?: boolean;
}

function Card({ item }: { item: GridItem }) {

    const orientationStyles = {
        horizontal: "aspect-[16/9]",
        vertical: "aspect-[3/4]",
        square: "aspect-square",
    }

    const aspect = orientationStyles[item.orientation || "vertical"]

    const content = (
        <div className={`relative ${aspect} overflow-hidden group`}>
            {
                item.image?.url && (
                    <Image
                        src={item.image.url}
                        alt={item.title || item.image.name}
                        fill
                        className="object-cover transition duration-500 group-hover:scale-105"
                    />
                )
            }

            {/* gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

            {/* content */}
            <div className="absolute inset-0 flex items-end justify-between p-6">
                {item.title && (
                    <Heading level={3} className="text-white text-right max-w-[70%]">
                        {item.title}
                    </Heading>
                )}

                {item.url && (
                    <ArrowLeft className='text-white group-hover:rotate-45 transition-all duration-200' size={20} />
                )}
            </div>
        </div >
    )

    if (item.url) {
        return (
            <Link href={item.url} className="block">
                {content}
            </Link>
        );
    }
    return content
}

export default function GridItems({ data, isHome = false }: GridItemsProps) {
    const count = data.items.length

    return (
        <div className="col-span-12 w-full p-4">
            {/* desktop grid */}
            <div className={`hidden lg:grid gap-4 ${count === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {data.items.map((item, index) => (
                    <Card key={index} item={item} />
                ))}
            </div>

            {/* mobile swiper */}
            <div className="lg:hidden">
                <Swiper
                    spaceBetween={12}
                    slidesPerView={1.1}
                    breakpoints={{
                        500: { slidesPerView: 1.5 },
                        640: { slidesPerView: count === 2 ? 2 : 2.2 },
                    }}
                    speed={700}
                    effect="fade"
                    fadeEffect={{
                        crossFade: true
                    }}
                >
                    {data.items.map((item, index) => (
                        <SwiperSlide key={index}>
                            <Card item={item} />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
}